import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  ArrowLeftIcon, 
  UserPlusIcon, 
  ShieldCheckIcon,
  UserIcon,
  TrashIcon,
  EnvelopeIcon,
  PencilIcon
} from '@heroicons/react/24/outline';
import { 
  organizersApi, 
  authApi,
  OrganizerListItem, 
  Organization,
  OrganizerProfile,
  OrganizerUpdate 
} from '../api/client';
import Logo from '../components/Logo';
import { getErrorMessage } from '../utils/errorHandler';

const OrganizersPage: React.FC = () => {
  const navigate = useNavigate();
  const [organizers, setOrganizers] = useState<OrganizerListItem[]>([]);
  const [organization, setOrganization] = useState<Organization | null>(null);
  const [currentUser, setCurrentUser] = useState<OrganizerProfile | null>(null);
  const [loading, setLoading] = useState(true);
  const [showInviteModal, setShowInviteModal] = useState(false);
  const [inviting, setInviting] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editing, setEditing] = useState(false);
  const [editingOrganizer, setEditingOrganizer] = useState<OrganizerListItem | null>(null);
  
  // Invite form state
  const [inviteForm, setInviteForm] = useState({
    email: '',
    name: '',
    role: 'organizer' as 'admin' | 'organizer'
  });
  
  // Edit form state
  const [editForm, setEditForm] = useState<OrganizerUpdate>({
    name: '',
    email: '',
    company: '',
    phone: '',
    job_title: ''
  });

  const loadData = async () => {
    try {
      const [organizersData, orgData, profileData] = await Promise.all([
        organizersApi.getOrganizers(),
        organizersApi.getOrganization(),
        authApi.getProfile()
      ]);
      
      setOrganizers(organizersData);
      setOrganization(orgData);
      setCurrentUser(profileData);
      
      // Check if current user has permission to view this page
      if (profileData.role === 'organizer') {
        navigate('/dashboard');
      }
    } catch (error) {
      console.error('Failed to load data:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    // No need to check for token - authentication is handled by httpOnly cookies
    // The API will return 401 if not authenticated, which is handled by the interceptor
    loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [navigate]);

  const handleInvite = async (e: React.FormEvent) => {
    e.preventDefault();
    setInviting(true);

    try {
      await organizersApi.inviteOrganizer(inviteForm);
      alert('Invitation sent successfully!');
      setShowInviteModal(false);
      setInviteForm({ email: '', name: '', role: 'organizer' });
      loadData();
    } catch (error) {
      alert(getErrorMessage(error));
    } finally {
      setInviting(false);
    }
  };

  const handleRoleChange = async (organizerId: number, newRole: 'admin' | 'organizer') => {
    if (!window.confirm('Are you sure you want to change this organizer\'s role?')) {
      return;
    }

    try {
      await organizersApi.updateOrganizerRole(organizerId, { role: newRole });
      loadData();
    } catch (error) {
      alert(getErrorMessage(error));
    }
  };

  const handleDeactivate = async (organizerId: number, name: string) => {
    if (!window.confirm(`Are you sure you want to deactivate ${name}? They will no longer be able to access the system.`)) {
      return;
    }

    try {
      await organizersApi.deactivateOrganizer(organizerId);
      loadData();
    } catch (error) {
      alert(getErrorMessage(error));
    }
  };

  const handleEditClick = (organizer: OrganizerListItem) => {
    setEditingOrganizer(organizer);
    setEditForm({
      name: organizer.name,
      email: organizer.email,
      company: organizer.company,
      phone: organizer.phone,
      job_title: organizer.job_title
    });
    setShowEditModal(true);
  };

  const handleEdit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!editingOrganizer) return;
    
    setEditing(true);

    try {
      await organizersApi.updateOrganizer(editingOrganizer.id, editForm);
      alert('Organizer updated successfully!');
      setShowEditModal(false);
      setEditingOrganizer(null);
      loadData();
    } catch (error) {
      alert(getErrorMessage(error));
    } finally {
      setEditing(false);
    }
  };

  const getRoleBadge = (role: string) => {
    switch (role) {
      case 'primary_admin':
        return <span className="px-2 py-1 text-xs font-medium bg-purple-100 text-purple-800 rounded-full">Primary Admin</span>;
      case 'admin':
        return <span className="px-2 py-1 text-xs font-medium bg-blue-100 text-blue-800 rounded-full">Admin</span>;
      default:
        return <span className="px-2 py-1 text-xs font-medium bg-gray-100 text-gray-800 rounded-full">Organizer</span>;
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (!currentUser || currentUser.role === 'organizer') {
    return null;
  }

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-6xl mx-auto px-4">
        <div className="flex justify-center mb-6">
          <Logo size="medium" showCompanyName={true} />
        </div>
        <div className="mb-6">
          <button
            onClick={() => navigate('/dashboard')}
            className="flex items-center text-gray-600 hover:text-gray-900"
          >
            <ArrowLeftIcon className="h-5 w-5 mr-1" />
            Back to Dashboard
          </button>
        </div>

        <div className="bg-white rounded-lg shadow">
          <div className="p-6 border-b">
            <div className="flex justify-between items-center">
              <div>
                <h1 className="text-2xl font-bold">Team Members</h1>
                {organization && (
                  <p className="text-gray-600 mt-1">{organization.name}</p>
                )}
              </div>
              <button
                onClick={() => setShowInviteModal(true)}
                className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
              >
                <UserPlusIcon className="h-5 w-5" />
                Invite Member
              </button>
            </div>
          </div>

          <div className="p-6">
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead>
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Name
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Email
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Role
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Joined
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {organizers.map((organizer) => (
                    <tr key={organizer.id} className={organizer.id === currentUser.id ? 'bg-blue-50' : ''}>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center">
                          <div className="h-10 w-10 flex-shrink-0">
                            <div className="h-10 w-10 rounded-full bg-gray-300 flex items-center justify-center">
                              <UserIcon className="h-6 w-6 text-gray-600" />
                            </div>
                          </div>
                          <div className="ml-4">
                            <div className="text-sm font-medium text-gray-900">
                              {organizer.name}
                              {organizer.id === currentUser.id && (
                                <span className="ml-2 text-xs text-gray-500">(You)</span>
                              )}
                            </div>
                            <div className="text-sm text-gray-500">{organizer.job_title}</div>
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center text-sm text-gray-900">
                          <EnvelopeIcon className="h-4 w-4 mr-1 text-gray-400" />
                          {organizer.email}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        {getRoleBadge(organizer.role)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {new Date(organizer.created_at).toLocaleDateString()}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                        {organizer.id !== currentUser.id && organizer.role !== 'primary_admin' && (
                          <div className="flex items-center gap-2">
                            {currentUser.role === 'primary_admin' && (
                              <select
                                value={organizer.role}
                                onChange={(e) => handleRoleChange(organizer.id, e.target.value as 'admin' | 'organizer')}
                                className="text-sm border border-gray-300 rounded px-2 py-1"
                              >
                                <option value="organizer">Organizer</option>
                                <option value="admin">Admin</option>
                              </select>
                            )}
                            <button
                              onClick={() => handleEditClick(organizer)}
                              className="text-blue-600 hover:text-blue-900"
                              title="Edit"
                            >
                              <PencilIcon className="h-5 w-5" />
                            </button>
                            <button
                              onClick={() => handleDeactivate(organizer.id, organizer.name)}
                              className="text-red-600 hover:text-red-900"
                              title="Deactivate"
                            >
                              <TrashIcon className="h-5 w-5" />
                            </button>
                          </div>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>

        {/* Role Descriptions */}
        <div className="mt-6 bg-white rounded-lg shadow p-6">
          <h2 className="text-lg font-semibold mb-4">Role Permissions</h2>
          <div className="space-y-3">
            <div className="flex items-start gap-3">
              <ShieldCheckIcon className="h-5 w-5 text-purple-600 mt-0.5" />
              <div>
                <h3 className="font-medium text-purple-800">Primary Admin</h3>
                <p className="text-sm text-gray-600">Full access to all features, can manage all team members including other admins</p>
              </div>
            </div>
            <div className="flex items-start gap-3">
              <ShieldCheckIcon className="h-5 w-5 text-blue-600 mt-0.5" />
              <div>
                <h3 className="font-medium text-blue-800">Admin</h3>
                <p className="text-sm text-gray-600">Can manage events, invitations, and invite new organizers</p>
              </div>
            </div>
            <div className="flex items-start gap-3">
              <UserIcon className="h-5 w-5 text-gray-600 mt-0.5" />
              <div>
                <h3 className="font-medium text-gray-800">Organizer</h3>
                <p className="text-sm text-gray-600">Can create and manage events, send invitations, and view reports</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Invite Modal */}
      {showInviteModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg max-w-md w-full p-6">
            <h2 className="text-xl font-semibold mb-4">Invite Team Member</h2>
            
            <form onSubmit={handleInvite} className="space-y-4">
              <div>
                <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-1">
                  Name <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  id="name"
                  required
                  value={inviteForm.name}
                  onChange={(e) => setInviteForm({ ...inviteForm, name: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>

              <div>
                <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1">
                  Email <span className="text-red-500">*</span>
                </label>
                <input
                  type="email"
                  id="email"
                  required
                  value={inviteForm.email}
                  onChange={(e) => setInviteForm({ ...inviteForm, email: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
                <p className="mt-1 text-xs text-gray-500">
                  Only @star.london email addresses are allowed
                </p>
              </div>

              <div>
                <label htmlFor="role" className="block text-sm font-medium text-gray-700 mb-1">
                  Role <span className="text-red-500">*</span>
                </label>
                <select
                  id="role"
                  value={inviteForm.role}
                  onChange={(e) => setInviteForm({ ...inviteForm, role: e.target.value as 'admin' | 'organizer' })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="organizer">Organizer</option>
                  <option value="admin">Admin</option>
                </select>
              </div>

              <div className="flex justify-end gap-3 pt-4">
                <button
                  type="button"
                  onClick={() => setShowInviteModal(false)}
                  className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={inviting}
                  className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:bg-gray-400"
                >
                  {inviting ? 'Sending...' : 'Send Invitation'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Edit Modal */}
      {showEditModal && editingOrganizer && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg max-w-md w-full p-6">
            <h2 className="text-xl font-semibold mb-4">Edit Team Member</h2>
            
            <form onSubmit={handleEdit} className="space-y-4">
              <div>
                <label htmlFor="edit-name" className="block text-sm font-medium text-gray-700 mb-1">
                  Name <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  id="edit-name"
                  required
                  value={editForm.name}
                  onChange={(e) => setEditForm({ ...editForm, name: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>

              <div>
                <label htmlFor="edit-email" className="block text-sm font-medium text-gray-700 mb-1">
                  Email <span className="text-red-500">*</span>
                </label>
                <input
                  type="email"
                  id="edit-email"
                  required
                  value={editForm.email}
                  onChange={(e) => setEditForm({ ...editForm, email: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>

              <div>
                <label htmlFor="edit-company" className="block text-sm font-medium text-gray-700 mb-1">
                  Company
                </label>
                <input
                  type="text"
                  id="edit-company"
                  value={editForm.company}
                  onChange={(e) => setEditForm({ ...editForm, company: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>

              <div>
                <label htmlFor="edit-phone" className="block text-sm font-medium text-gray-700 mb-1">
                  Phone
                </label>
                <input
                  type="tel"
                  id="edit-phone"
                  value={editForm.phone}
                  onChange={(e) => setEditForm({ ...editForm, phone: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>

              <div>
                <label htmlFor="edit-job-title" className="block text-sm font-medium text-gray-700 mb-1">
                  Job Title
                </label>
                <input
                  type="text"
                  id="edit-job-title"
                  value={editForm.job_title}
                  onChange={(e) => setEditForm({ ...editForm, job_title: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>

              <div className="flex justify-end gap-3 pt-4">
                <button
                  type="button"
                  onClick={() => {
                    setShowEditModal(false);
                    setEditingOrganizer(null);
                  }}
                  className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={editing}
                  className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:bg-gray-400"
                >
                  {editing ? 'Saving...' : 'Save Changes'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default OrganizersPage;